import React, { useEffect, useMemo, useState } from 'react';
import CompetenceMonthFilter from '../../components/common/CompetenceMonthFilter/CompetenceMonthFilter';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import FormInput from '../../components/common/FormInput/FormInput';
import Loading from '../../components/common/Loading/Loading';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import ViewLinkButton from '../../components/common/ViewLinkButton/ViewLinkButton';
import { COMMERCIAL_GOALS_PERIOD_PRESETS, detectDatePreset } from '../../constants/datePresets';
import { useAuth } from '../../contexts/AuthContext';
import { useCommercialGoalsDashboard } from '../../hooks/useCommercialGoalsDashboard';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { CommercialSalesGoalService } from '../../services/CommercialSalesGoalService';
import {
  COMMERCIAL_GOALS_PERIODS,
  resolveCommercialGoalsQuery,
  type CommercialGoalsPeriod,
} from '../../utils/commercialGoalsDashboard';
import {
  getBrazilCurrentCompetenceMonth,
  getBrazilMonthBounds,
  getBrazilPreviousCompetenceMonth,
} from '../../utils/competence';
import { canConfigureSalesGoals } from '../../utils/permissions';
import CommercialGoalsBoard from './components/CommercialGoalsBoard';
import dashboardStyles from './Dashboard.module.css';
import styles from './SalesGoalsPanel.module.css';

const COMMERCIAL_GOALS_FILTER_SCHEMA = {
  period: queryFilter.enum<CommercialGoalsPeriod>(
    'currentMonth',
    COMMERCIAL_GOALS_PERIODS,
    { param: 'periodo' },
  ),
  dateStart: queryFilter.string('', { param: 'de', trim: true }),
  dateEnd: queryFilter.string('', { param: 'ate', trim: true }),
};

const isCommercialGoalsPeriod = (value: string): value is CommercialGoalsPeriod => (
  (COMMERCIAL_GOALS_PERIODS as readonly string[]).includes(value)
);

const SalesGoalsPanel: React.FC = () => {
  const { roles } = useAuth();
  const { filters, setFilters } = useQueryFilters(COMMERCIAL_GOALS_FILTER_SCHEMA);
  const [configOpen, setConfigOpen] = useState(false);
  const resolved = useMemo(
    () => resolveCommercialGoalsQuery(filters.period, filters.dateStart, filters.dateEnd),
    [filters.dateEnd, filters.dateStart, filters.period],
  );
  const query = resolved.ok ? resolved.query : null;
  const { snapshot, loading, error, refetch } = useCommercialGoalsDashboard(query);
  const canConfigure = canConfigureSalesGoals(roles);
  const goalMonth = query?.goalMonth ?? getBrazilCurrentCompetenceMonth();

  const handlePeriodChange = (start: string, end: string) => {
    const detected = detectDatePreset(start, end, COMMERCIAL_GOALS_PERIOD_PRESETS);
    if (detected === 'custom' || !isCommercialGoalsPeriod(detected)) {
      setFilters({ period: 'custom', dateStart: start, dateEnd: end });
      return;
    }

    setFilters({ period: detected, dateStart: '', dateEnd: '' });
  };

  return (
    <section className={styles.panel}>
      <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
        <RecordCount label="Vendas:" count={snapshot?.salesInPeriod ?? 0} />
        <FilterGroup>
          <DatePresetFilter
            dateStart={query?.dateStart ?? filters.dateStart}
            dateEnd={query?.dateEnd ?? filters.dateEnd}
            onChange={handlePeriodChange}
            placeholder="Período"
            presetOptions={COMMERCIAL_GOALS_PERIOD_PRESETS}
          />
        </FilterGroup>
        {canConfigure && (
          <ViewLinkButton
            label="Configurar metas"
            icon="settings"
            onClick={() => setConfigOpen(true)}
          />
        )}
      </FilterBar>

      {!resolved.ok && <EmptyState message={resolved.message} showImage={false} />}
      {resolved.ok && loading && !snapshot && <Loading />}
      {resolved.ok && error && <EmptyState message={error} showImage={false} />}
      {resolved.ok && snapshot && <CommercialGoalsBoard snapshot={snapshot} />}

      {canConfigure && (
        <ConfigureSalesGoalModal
          open={configOpen}
          month={goalMonth}
          onClose={() => setConfigOpen(false)}
          onSaved={(savedMonth) => {
            setConfigOpen(false);
            const currentMonth = getBrazilCurrentCompetenceMonth();
            const previousMonth = getBrazilPreviousCompetenceMonth();
            if (savedMonth === currentMonth) {
              setFilters({ period: 'currentMonth', dateStart: '', dateEnd: '' });
            } else if (savedMonth === previousMonth) {
              setFilters({ period: 'previousMonth', dateStart: '', dateEnd: '' });
            } else {
              const bounds = getBrazilMonthBounds(savedMonth);
              if (bounds) {
                setFilters({ period: 'custom', dateStart: bounds.start, dateEnd: bounds.end });
              }
            }
            refetch();
          }}
        />
      )}
    </section>
  );
};

interface ConfigureSalesGoalModalProps {
  open: boolean;
  month: string;
  onClose: () => void;
  onSaved: (month: string) => void;
}

const ConfigureSalesGoalModal: React.FC<ConfigureSalesGoalModalProps> = ({
  open,
  month,
  onClose,
  onSaved,
}) => {
  const [draftMonth, setDraftMonth] = useState(month);
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraftMonth(month);
    setError(null);
  }, [open, month]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let ignore = false;
    CommercialSalesGoalService.get(draftMonth)
      .then((next) => {
        if (!ignore) {
          setTarget(next.targetSales != null ? String(next.targetSales) : '');
        }
      })
      .catch(() => {
        if (!ignore) {
          setTarget('');
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, draftMonth]);

  const handleConfirm = async () => {
    if (!/^\d{4}-\d{2}$/.test(draftMonth)) {
      setError('Informe o mês.');
      return;
    }

    const parsedTarget = Number(target);
    if (target.trim() === '' || !Number.isInteger(parsedTarget) || parsedTarget < 0) {
      setError('Informe a quantidade de vendas como um número inteiro maior ou igual a zero.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await CommercialSalesGoalService.save(draftMonth, parsedTarget);
      onSaved(saved.month);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Não foi possível salvar a meta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmModal
      open={open}
      title="Configurar metas"
      variant="info"
      icon="flag"
      alignment="left"
      confirmLabel="Salvar"
      loading={saving}
      onConfirm={() => {
        void handleConfirm();
      }}
      onCancel={onClose}
    >
      <div className={styles.form}>
        <label className={styles.field}>
          <span className={styles.label}>Mês</span>
          <CompetenceMonthFilter
            value={draftMonth}
            onChange={setDraftMonth}
            ariaLabel="Mês da meta"
            disabled={saving}
          />
        </label>
        <FormInput
          id="sales-goal-target"
          label="Quantidade de vendas"
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          value={target}
          disabled={saving}
          error={error ?? undefined}
          onChange={(event) => setTarget(event.target.value)}
        />
      </div>
    </ConfirmModal>
  );
};

export default SalesGoalsPanel;
